// Home — TSE Workspace dashboard. Keeps the original letterhead / expense /
// vendor-payment summary and adds the workspace request, approvals, wall and
// celebrations widgets in the TSE design.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { expenseAPI, letterheadAPI, vendorAPI, wsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/roles';
import {
  PageShell, Card, Stat, Empty, Loading, ReqStatus, useAsync, money, pad2, fmtDate, fmtDateShort, timeAgo, Avatar,
} from '../components/ui';
import { TYPE_LABELS } from '../components/RequestDetail';

const QUICK = [
  { key: 'etams.travel', to: '/etams/travel-requests/new', icon: 'bi-airplane', label: 'New travel request', sub: 'eTAMS · TAE' },
  { key: 'expense.claim', to: '/etams/claims/new', icon: 'bi-receipt', label: 'Create expense claim', sub: 'eTAMS · claim' },
  { key: 'expense.advance', to: '/expense/advance-request', icon: 'bi-wallet2', label: 'Request advance', sub: 'Travel advance' },
  { key: 'letterhead.editor', to: '/letterhead/editor', icon: 'bi-file-earmark-plus', label: 'New letterhead', sub: 'Letterhead' },
  { key: 'workspace.servicehub', to: '/service-hub', icon: 'bi-grid', label: 'Service Hub', sub: 'Department services' },
  { key: 'it.asset', to: '/it-facilities/it-asset', icon: 'bi-laptop', label: 'IT asset request', sub: 'IT & Facilities' },
  { key: 'workspace.requests', to: '/requests/memos', icon: 'bi-file-earmark-text', label: 'Approval memo', sub: 'Internal requests' },
  { key: 'workspace.policies', to: '/policies', icon: 'bi-book', label: 'Policy Center', sub: 'Policies & forms' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [expStats, setExpStats] = useState({ advances: 0, claims: 0, pendingApprovals: 0, settled: 0 });
  const [lhStats, setLhStats] = useState({ pendingRM: 0, pendingHOD: 0, approved: 0, total: 0 });
  const [vpStats, setVpStats] = useState({ pending: 0, paid: 0, overdueGRN: 0, processed: 0 });
  const [loading, setLoading] = useState(true);
  const reqStats = useAsync(() => wsAPI.requestStats(), [], { silent: true });
  const inbox = useAsync(() => wsAPI.inbox(), [], { silent: true });
  const recent = useAsync(() => wsAPI.listRequests({ scope: 'mine', limit: 6 }), [], { silent: true });
  const posts = useAsync(() => wsAPI.posts({ limit: 3 }), [], { silent: true });
  const events = useAsync(() => wsAPI.events({ upcoming: 1 }), [], { silent: true });
  const celebrations = useAsync(() => wsAPI.celebrations(), [], { silent: true });
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  useEffect(() => {
    (async () => {
      try {
        const [advData, claimData, lhData, grnData, payData] = await Promise.allSettled([
          canAccess(user, 'expense.advance') ? expenseAPI.listAdvances() : Promise.reject(),
          canAccess(user, 'expense.claim') ? expenseAPI.listClaims() : Promise.reject(),
          canAccess(user, 'letterhead.history') ? letterheadAPI.listRequests({ limit: 100 }) : Promise.reject(),
          canAccess(user, 'vendor.grn') ? vendorAPI.listGRN({ status: 'PENDING' }) : Promise.reject(),
          canAccess(user, 'vendor.released') ? vendorAPI.listReleased() : Promise.reject(),
        ]);
        if (advData.status === 'fulfilled') {
          const advs = advData.value.requests || [];
          setExpStats((p) => ({ ...p, advances: advs.length, pendingApprovals: advs.filter((a) => ['PENDING_MANAGER', 'PENDING_FINANCE'].includes(a.status)).length }));
        }
        if (claimData.status === 'fulfilled') {
          const cls = claimData.value.claims || [];
          const settled = cls.filter((c) => c.status === 'SETTLED');
          setExpStats((p) => ({ ...p, claims: cls.length, settled: settled.reduce((s, c) => s + (c.settlementAmount || 0), 0) }));
        }
        if (lhData.status === 'fulfilled') {
          const reqs = lhData.value.requests || [];
          setLhStats({ pendingRM: reqs.filter((r) => r.status === 'UNDER_RM').length, pendingHOD: reqs.filter((r) => r.status === 'UNDER_HOD').length, approved: reqs.filter((r) => r.status === 'APPROVED').length, total: reqs.length });
        }
        if (grnData.status === 'fulfilled') setVpStats((p) => ({ ...p, overdueGRN: grnData.value.total || 0 }));
        if (payData.status === 'fulfilled') {
          const paid = payData.value.requests || [];
          setVpStats((p) => ({ ...p, paid: payData.value.totalPaid || 0, processed: paid.length }));
        }
      } catch (e) {
        // individual widgets fall back to zero
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rs = reqStats.data?.stats || {};
  const items = inbox.data?.items || [];
  const greet = `Good ${now.getHours() < 12 ? 'morning' : now.getHours() < 17 ? 'afternoon' : 'evening'}, ${user?.userName?.split(' ')[0] || ''}`;
  const quick = QUICK.filter((q) => canAccess(user, q.key));
  const cel = celebrations.data || {};
  const celList = [...(cel.birthdays || []).map((b) => ({ ...b, what: 'Birthday', icon: 'bi-cake2' })), ...(cel.anniversaries || []).map((a) => ({ ...a, what: `${a.years} yr${a.years > 1 ? 's' : ''} at TSE`, icon: 'bi-award' }))].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 4);

  return (
    <PageShell section="TSE Workspace">
      <div className="tse-hero mb-18">
        <i className="bi bi-droplet-half ic" />
        <div>
          <div className="h">{greet}</div>
          <div className="s">{dateStr} · {user?.designation || ''}{user?.department ? ` · ${user.department}` : ''}</div>
        </div>
        <div className="tag">People · Water<br />A Better Tomorrow</div>
      </div>

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-file-earmark-text" tone="blue" label="My open requests" value={pad2(rs.open)} loading={reqStats.loading} to="/requests/my" />
        <Stat icon="bi-person-check" tone="red" label="Awaiting my action" value={pad2(items.length)} loading={inbox.loading} to="/approvals" />
        <Stat icon="bi-calendar-check" tone="green" label="Due today" value={pad2(rs.dueToday)} loading={reqStats.loading} to="/requests/my" />
        <Stat icon="bi-check-circle" tone="purple" label="Closed this month" value={pad2(rs.closedThisMonth)} loading={reqStats.loading} to="/requests/my?status=closed" />
      </div>

      <div className="tse-grid g-4 mb-18">
        {canAccess(user, 'letterhead.history') && <Stat compact icon="bi-file-richtext" tone="navy" label="Total letterheads" value={loading ? '—' : lhStats.total} foot={`${lhStats.pendingRM + lhStats.pendingHOD} pending · ${lhStats.approved} approved`} to="/letterhead/history" />}
        {canAccess(user, 'expense.advance') && <Stat compact icon="bi-wallet2" tone="blue" label="Advance requests" value={loading ? '—' : expStats.advances} foot={`${expStats.pendingApprovals} awaiting approval`} to="/expense/advance-request" />}
        {canAccess(user, 'expense.claim') && <Stat compact icon="bi-receipt" tone="teal" label="Expense claims" value={loading ? '—' : expStats.claims} foot={`${money(expStats.settled)} settled`} to="/expense/claim-submission" />}
        {(canAccess(user, 'vendor.released') || canAccess(user, 'vendor.grn')) && <Stat compact icon="bi-shop" tone="amber" label="Payments released" value={loading ? '—' : vpStats.processed} foot={`${money(vpStats.paid)} paid · ${vpStats.overdueGRN} GRN pending`} to={canAccess(user, 'vendor.released') ? '/vendor/payment-released' : '/vendor/grn-verification'} />}
      </div>

      <div className="tse-split mb-18" style={{ gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', alignItems: 'start' }}>
        <div className="tse-stack" style={{ gap: 8 }}>
          <Card title="Quick actions" bodyClass="tight">
            <div className="tse-grid g-4" style={{ gap: 8 }}>
              {quick.map((q) => (
                <Link key={q.to} to={q.to} className="tse-tile"><span className="ic blue"><i className={`bi ${q.icon}`} /></span><div><div className="t">{q.label}</div><div className="s">{q.sub}</div></div></Link>
              ))}
            </div>
          </Card>
          <Card title="Awaiting my action" actions={<Link to="/approvals" className="tse-link">Open inbox <i className="bi bi-arrow-right" /></Link>} bodyClass="tight">
            {inbox.loading ? <Loading /> : !items.length ? <Empty icon="bi-check2-circle" title="You're all caught up" /> : (
              <div className="tse-table-wrap"><table className="tse-table compact">
                <thead><tr><th>Reference</th><th>Type</th><th>Requester</th><th>Amount</th><th>Submitted</th></tr></thead>
                <tbody>{items.slice(0, 5).map((i) => (
                  <tr key={`${i.source}-${i.id}`}><td><Link to={i.link} className="tse-link">{i.refNo || '—'}</Link></td><td>{i.kind}</td><td>{i.requester}</td><td>{i.amount ? money(i.amount) : '—'}</td><td>{i.submittedAt ? timeAgo(i.submittedAt) : '—'}</td></tr>
                ))}</tbody>
              </table></div>
            )}
          </Card>
          <Card title="My recent requests" actions={<Link to="/requests/my" className="tse-link">View all <i className="bi bi-arrow-right" /></Link>} bodyClass="tight">
            {recent.loading ? <Loading /> : !(recent.data?.requests || []).length ? <Empty icon="bi-list-check" title="No requests yet" sub="Raise one from the Service Hub." /> : (
              <div className="tse-table-wrap"><table className="tse-table compact">
                <thead><tr><th>Request ID</th><th>Type</th><th>Title</th><th>Status</th><th>Updated</th></tr></thead>
                <tbody>{recent.data.requests.slice(0, 5).map((r) => (
                  <tr key={r._id}><td><Link to={`/requests/my?id=${r._id}`} className="tse-link">{r.refNo || 'Draft'}</Link></td><td>{TYPE_LABELS[r.type]}</td><td>{r.title}</td><td><ReqStatus request={r} /></td><td>{fmtDate(r.updatedAt)}</td></tr>
                ))}</tbody>
              </table></div>
            )}
          </Card>
        </div>

        <div className="tse-stack" style={{ gap: 8 }}>
          <Card title="Latest on the wall" actions={<Link to="/employee-wall" className="tse-link">Employee Wall <i className="bi bi-arrow-right" /></Link>} bodyClass="tight">
            {posts.loading ? <Loading /> : !(posts.data?.posts || []).length ? <Empty icon="bi-megaphone" title="No posts yet" /> : posts.data.posts.slice(0, 3).map((p) => (
              <div key={p._id} className="tse-row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 8, padding: '7px 0', borderBottom: '1px solid #eef2f6' }}>
                <Avatar name={p.authorLabel || p.authorName} size="sm" />
                <div style={{ minWidth: 0 }}>
                  <div className="tse-strong">{p.title || p.authorLabel || p.authorName}</div>
                  <div className="tse-small" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.content}</div>
                  <div className="tse-small tse-muted">{p.authorName} · {timeAgo(p.createdAt)}</div>
                </div>
              </div>
            ))}
          </Card>
          <Card title="Upcoming events" bodyClass="tight">
            {events.loading ? <Loading /> : !(events.data?.events || []).length ? <Empty icon="bi-calendar-event" title="No upcoming events" /> : events.data.events.slice(0, 3).map((e) => (
              <div key={e._id} className="tse-row" style={{ flexWrap: 'nowrap', gap: 8, padding: '6px 0' }}>
                <div className="tse-datebox"><span className="d">{new Date(e.date).getDate()}</span><span className="m">{fmtDateShort(e.date).split(' ')[1]}</span></div>
                <div><div className="tse-strong">{e.title}</div><div className="tse-small">{[e.time, e.location].filter(Boolean).join(' · ')}</div></div>
              </div>
            ))}
          </Card>
          <Card title="Celebrations" bodyClass="tight">
            {celebrations.loading ? <Loading /> : !celList.length ? <Empty icon="bi-balloon" title="No celebrations this month" /> : celList.map((c) => (
              <div key={`${c.id}-${c.what}`} className="tse-row" style={{ flexWrap: 'nowrap', gap: 8, padding: '5px 0' }}>
                <Avatar name={c.userName} size="sm" />
                <div style={{ flex: 1 }}><div className="tse-strong">{c.userName}</div><div className="tse-small">{c.designation}</div></div>
                <span className="tse-small"><i className={`bi ${c.icon}`} style={{ color: '#1479bd' }} /> {c.what} · {fmtDateShort(c.date)}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </PageShell>
  );
}
